import { Link } from "react-router-dom";
import { BrandMark } from "@/components/BrandMark";
import ThemePicker from "@/components/dashboard/ThemePicker";

function LegalLayout({ title, updated, children, testId }) {
  return (
    <div className="min-h-screen bg-bg text-ink" data-testid={testId}>
      <header className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="legal-brand-link">
          <BrandMark size={32} />
          <span className="font-display text-xl font-semibold tracking-tight">Ikavio</span>
        </Link>
        <ThemePicker />
      </header>
      <main className="max-w-3xl mx-auto px-6 pb-20">
        <h1 className="font-display font-light tracking-tighter text-4xl sm:text-5xl mt-4">{title}</h1>
        <p className="text-sm text-muted2 mt-2">Last updated: {updated}</p>
        <div className="mt-8 space-y-8">{children}</div>
      </main>
      <footer className="border-t border-border py-8 text-center text-sm text-muted2">
        <div className="flex items-center justify-center gap-5 flex-wrap">
          <span>Ikavio — Optimizing for healthy, capable years of life.</span>
          <Link to="/privacy" className="text-brand hover:underline font-medium">Privacy</Link>
          <Link to="/terms" className="text-brand hover:underline font-medium">Terms</Link>
        </div>
      </footer>
    </div>
  );
}

function S({ h, children }) {
  return (
    <section>
      <h2 className="font-display text-lg font-medium">{h}</h2>
      <div className="text-sm text-muted2 leading-relaxed mt-2 space-y-2">{children}</div>
    </section>
  );
}

export function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated="June 2026" testId="privacy-page">
      <S h="Who we are">
        <p>Ikavio ("we", "us") operates the Ikavio Human Performance OS at https://ikavio.com — a personal health and wellbeing application. This policy explains what data we collect, why, and the choices you have. Contact: privacy@ikavio.com.</p>
      </S>
      <S h="Data we collect">
        <p><strong className="text-ink">Account data:</strong> name, email address and a securely hashed password (we never store plain-text passwords), or your Google account identity if you sign in with Google.</p>
        <p><strong className="text-ink">Health & lifestyle data you provide:</strong> daily check-ins (sleep, mood, stress, energy, soreness), profile details (age, goals, activity level, injuries) and plan feedback. You choose what to share.</p>
        <p><strong className="text-ink">Wearable data:</strong> only if you explicitly connect a device (Oura, Google Health, Apple Health). We read the minimum metrics needed (sleep, heart rate, HRV, steps) and you can disconnect at any time, which stops all syncing.</p>
      </S>
      <S h="How we use your data">
        <p>Exclusively to run Ikavio for you: generating your daily plans, Healthspan Score, Digital Twin insights, coaching responses and optional notifications. AI features process your data solely to produce your personal guidance.</p>
        <p><strong className="text-ink">We do not sell your data. We do not show ads. We never share health data with advertisers, data brokers or employers.</strong></p>
      </S>
      <S h="Storage & security">
        <p>Data is stored encrypted in transit (TLS) and protected at rest. Access is limited to what is required to operate the service. Wearable OAuth tokens are stored server-side and never exposed to other users.</p>
      </S>
      <S h="Your rights">
        <p>You can access, correct or export your profile data in the app, and delete your account and all associated data at any time from Profile → account settings. If you are in the EU/EEA, you have rights under the GDPR including access, rectification, erasure, portability and objection. Write to privacy@ikavio.com to exercise any right.</p>
      </S>
      <S h="Cookies & analytics">
        <p>We use essential storage (login session, theme preference) and privacy-respecting product analytics to understand feature usage. We do not use third-party advertising cookies.</p>
      </S>
      <S h="Not medical advice">
        <p>Ikavio provides general wellness guidance and is not a medical device or a substitute for professional medical advice, diagnosis or treatment. Always consult a qualified health professional for medical concerns.</p>
      </S>
      <S h="Changes">
        <p>We will update this policy as the product evolves and note the revision date above. Material changes will be announced in the app.</p>
      </S>
    </LegalLayout>
  );
}

export function Terms() {
  return (
    <LegalLayout title="Terms of Service" updated="June 2026" testId="terms-page">
      <S h="1. Agreement">
        <p>By creating an account or using Ikavio (https://ikavio.com) you agree to these Terms. If you do not agree, please do not use the service.</p>
      </S>
      <S h="2. The service">
        <p>Ikavio is a personal health and performance application offering adaptive daily plans, a Healthspan Score, a predictive Digital Twin, AI coaching and optional wearable integrations. Core features are free. Ikavio Pro is a paid subscription (monthly or yearly, with a 7-day full trial for new members) that unlocks premium features; current pricing is shown at checkout and on our pricing page. You can cancel your subscription at any time, effective at the end of the current billing period. Payments are processed securely by Stripe.</p>
      </S>
      <S h="3. Not medical advice">
        <p>Ikavio provides general wellness and lifestyle guidance only. It is not a medical device and does not provide medical advice, diagnosis or treatment. Consult a physician before starting any exercise or nutrition program, and seek immediate professional help for any medical emergency or mental health crisis.</p>
      </S>
      <S h="4. Your account">
        <p>You must be at least 16 years old. You are responsible for keeping your credentials confidential and for the accuracy of the information you provide. You may delete your account at any time.</p>
      </S>
      <S h="5. Acceptable use">
        <p>You agree not to misuse the service, attempt unauthorized access, scrape other users' data, or use Ikavio to harm yourself or others.</p>
      </S>
      <S h="6. Your data">
        <p>Your content and health data remain yours. Our use of data is described in the <Link to="/privacy" className="text-brand hover:underline">Privacy Policy</Link>. We do not sell personal data.</p>
      </S>
      <S h="7. Availability & changes">
        <p>We aim for high availability but the service is provided "as is" without warranties. Features may change as the product evolves. We may suspend accounts that violate these Terms.</p>
      </S>
      <S h="8. Liability">
        <p>To the maximum extent permitted by law, Ikavio is not liable for indirect or consequential damages arising from use of the service. Nothing in these Terms limits liability that cannot be limited by law.</p>
      </S>
      <S h="9. Contact">
        <p>Questions about these Terms: legal@ikavio.com.</p>
      </S>
    </LegalLayout>
  );
}
